import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  TextInput,
  Button,
  Stack,
  Text,
  PinInput,
  Group,
  Anchor,
  Alert,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { login, verifyOTP, resetTwoFactor, fetchUserProfile } from '../../store/slices/authSlice';
import { IconInfoCircle } from '@tabler/icons-react';

export const AdminLogin: React.FC = () => {
  const [step, setStep] = useState<'credentials' | 'otp'>('credentials');
  const [loginValue, setLoginValue] = useState('');
  const [userId, setUserId] = useState<string>(''); // ✅ Explicitly string, not undefined
  const [otpValue, setOtpValue] = useState('');
  
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, requiresTwoFactor, error } = useAppSelector((state) => state.auth);

  const loginForm = useForm({
    initialValues: {
      login: '',
      password: '',
    },
    validate: {
      login: (value) => (value.length < 3 ? 'Username or email is too short' : null),
      password: (value) => (value.length < 6 ? 'Password must be at least 6 characters' : null),
    },
  });

  const handleLoginSubmit = async (values: typeof loginForm.values) => {
    try {
      const result = await dispatch(login(values)).unwrap();
      
      if (result.requiresTwoFactor) {
        setLoginValue(values.login);
        if (result.userId) setUserId(result.userId); // ✅ only set if defined
        setStep('otp');
      } else {
        // Fetch full user profile before navigating
        await dispatch(fetchUserProfile());
        navigate('/admin/dashboard');
      }
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  const handleOtpSubmit = async (otp: string) => {
    if (!userId) {
      console.error('No user ID for OTP verification');
      return;
    }
    try {
      const result = await dispatch(verifyOTP({ userId, otpCode: otp })).unwrap();
      if (result.success) {
        await dispatch(fetchUserProfile());
        navigate('/admin/dashboard');
      }
    } catch (error) {
      console.error('OTP verification failed:', error);
    }
  };

  const handleResendOtp = async () => {
    try {
      await dispatch(login({ login: loginValue, password: loginForm.values.password })).unwrap();
    } catch (error) {
      console.error('Failed to resend OTP:', error);
    }
  };

  const handleBackToLogin = () => {
    setStep('credentials');
    dispatch(resetTwoFactor());
  };

  return (
    <Container size="xs" py={50}>
      <Paper radius="lg" p="xl" withBorder shadow="md">
        <Title ta="center" order={2} mb="md">
          Admin Login
        </Title>
        <Text ta="center" c="dimmed" size="sm" mb="xl">
          Thalvora Exports Administration
        </Text>

        {error && (
          <Alert color="red" mb="md" icon={<IconInfoCircle />}>
            {error}
          </Alert>
        )}

        {step === 'credentials' ? (
          <form onSubmit={loginForm.onSubmit(handleLoginSubmit)}>
            <Stack>
              <TextInput
                label="Username or Email"
                placeholder="Enter your username or email"
                {...loginForm.getInputProps('login')}
                required
              />
              
              <TextInput
                label="Password"
                type="password"
                placeholder="Enter your password"
                {...loginForm.getInputProps('password')}
                required
              />

              <Group justify="flex-end">
                <Anchor 
                  component="button" 
                  type="button" 
                  onClick={() => navigate('/admin/forgot-password')}
                  size="sm"
                >
                  Forgot password?
                </Anchor>
              </Group>

              <Button 
                type="submit" 
                color="exportGreen" 
                fullWidth 
                size="md"
                loading={loading}
              >
                Login
              </Button>
            </Stack>
          </form>
        ) : (
          <Stack align="center" gap="lg">
            <div>
              <Text size="sm" c="dimmed" ta="center">
                Two-Factor Authentication Required
              </Text>
              <Text size="sm" fw={500} ta="center">
                Enter the 6-digit code from your authenticator app
              </Text>
              <Text size="xs" c="dimmed" ta="center" mt={5}>
                A verification code has been sent to your email
              </Text>
            </div>

            <Group justify="center">
              <PinInput
                length={6}
                type="number"
                value={otpValue}
                onChange={setOtpValue}
                onComplete={handleOtpSubmit}
                disabled={loading}
                autoFocus
              />
            </Group>

            <Group justify="center" mt="md">
              <Button
                variant="subtle"
                onClick={handleBackToLogin}
                size="sm"
              >
                Back to Login
              </Button>
              <Button
                variant="light"
                color="exportGreen"
                size="sm"
                onClick={handleResendOtp}
                loading={loading}
              >
                Resend Code
              </Button>
            </Group>
          </Stack>
        )}
      </Paper>
    </Container>
  );
};